import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import {
    MsalModule,
    MsalInterceptor,
    MsalRedirectComponent,
    MSAL_INSTANCE,
    MSAL_GUARD_CONFIG,
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuard,
    MsalBroadcastService,
    MsalService

} from '@azure/msal-angular';

import { LuxonModule } from 'luxon-angular';

// PrimeNg Modules
import { AccordionModule } from "primeng/accordion";
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SplitButtonModule } from 'primeng/splitbutton';
import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { TabMenuModule } from 'primeng/tabmenu';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from "primeng/confirmdialog";

import { DigitOnlyModule } from '@uiowa/digit-only';

import { SharedModule } from '../../../shared/src/public_api';
import { SharedSettings } from 'projects/shared/src/lib/models/shared-settings';
import { ApiInterceptor } from '../../../shared/src/lib/services/api.interceptor';
import { AuthService } from '../../../shared/src/lib/authentication/auth.service';
import { MsalInstanceFactory, MsalInterceptorConfigFactory, MsalGuardConfigFactory } from 'projects/shared/src/lib/authentication/msal-factories';
import { LoggingService } from 'projects/shared/src/lib/services/logging.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { HomeComponent } from './pages/home/home.component';
import { InboundLoadsComponent } from './pages/inbound-loads/inbound-loads.component';
import { InboundLoadsFormComponent } from './pages/inbound-loads/form/form.component';
import { OutboundLoadsComponent } from './pages/outbound-loads/outbound-loads.component';
import { OutboundLoadsFormComponent } from './pages/outbound-loads/form/form.component';
import { InventoryComponent } from './pages/inventory/inventory.component';
import { PalletReclassificationComponent } from './pages/pallet-reclassification/pallet-reclassification.component';


import { CompleteShiftComponent } from './pages/complete-shift/complete-shift.component';
// CompleteShift Components
import { LoadsSummaryComponent } from './pages/complete-shift/components/loads-summary/loads-summary.component';
import { ComponentsSummaryComponent } from './pages/complete-shift/components/components-summary/components-summary.component';
import { InternalTransfersComponent } from './pages/complete-shift/components/internal-transfers/internal-transfers.component';
import { SortSummaryComponent } from './pages/complete-shift/components/sort-summary/sort-summary.component';
import { ChepActivityComponent } from './pages/complete-shift/components/chep-activity/chep-activity.component';


//
import { PieceRateFormComponent } from './pages/piece-rate-form/piece-rate-form.component';

import { ContextToolbarComponent } from './components/context-toolbar/context-toolbar.component';
import { AddCarrierFormComponent } from './components/add-carrier-form/add-carrier-form.component';
import { environment } from '@environment';
import { AddShippedToFormComponent } from './components/add-shipped-to-form/add-shipped-to-form.component';

// Daily Production Components
import { ChepMiscInfoComponent } from './pages/inventory/components/chep-misc-info/chep-misc-info.component';
import { TeardownsScrapComponent } from './pages/inventory/components/teardowns-scrap/teardowns-scrap.component';
import { BayInventoryComponent } from './pages/inventory/components/bay-inventory/bay-inventory.component';
import { RepairLineInventoryComponent } from './pages/inventory/components/repair-line-inventory/repair-line-inventory.component';
import { SortLineInventoryComponent } from './pages/inventory/components/sort-line-inventory/sort-line-inventory.component';
import { WalmartMiscInfoComponent } from './pages/inventory/components/walmart-misc-info/walmart-misc-info.component';
import { TpmInventoryComponent } from './pages/inventory/components/tpm-inventory/tpm-inventory.component';
import { ComponentsInventoryComponent } from './pages/inventory/components/components-inventory/components-inventory.component';
import { SuppliesInventoryComponent } from './pages/inventory/components/supplies-inventory/supplies-inventory.component';
import { TpmTrailerSummaryComponent } from './pages/inventory/components/tpm-trailer-summary/tpm-trailer-summary.component';

//
import { ShipLoadComponent } from './pages/outbound-loads/components/ship-load/ship-load.component';
import { CancelLoadComponent } from './pages/outbound-loads/components/cancel-load/cancel-load.component';
import { ArriveInboundComponent } from './pages/inbound-loads/dialogs/arrive-inbound/arrive-inbound.component';
import { ProcessInboundComponent } from './pages/inbound-loads/dialogs/process-inbound/process-inbound.component';
import { UnloadInboundComponent } from './pages/inbound-loads/dialogs/unload-inbound/unload-inbound.component';

import { PieceRateEntriesComponent } from './pages/piece-rate-form/components/piece-rate-entries/piece-rate-entries.component';
import { PieceRateSummaryComponent } from './pages/piece-rate-form/components/piece-rate-summary/piece-rate-summary.component';
import { PieceRateWeekStatusComponent } from './pages/piece-rate-form/components/piece-rate-week-status/piece-rate-week-status.component';
import { PieceRateAdjustmentCalculationComponent } from './pages/piece-rate-form/components/piece-rate-adjustment-calculation/piece-rate-adjustment-calculation.component';
import { QualityControlConfigComponent } from './pages/quality-control-config/quality-control-config.component';

import { PieceRateResetComponent } from './pages/piece-rate-form/components/piece-rate-reset/piece-rate-reset.component';
import { WorkedShiftSelectorComponent } from './components/worked-shift-selector/worked-shift-selector.component';
import { WwComponentsComponent } from './pages/complete-shift/components/ww-components/ww-components.component';
import {CreateOutboundLoadFromTemplateComponent} from "./pages/outbound-loads/components/create-outbound-load-from-template/create-outbound-load-from-template.component";
// Power-Bi
import { InputTextareaModule } from "primeng/inputtextarea";
import { PieceRateGroupTotalsComponent } from './pages/piece-rate-form/components/piece-rate-group-totals/piece-rate-group-totals.component';
import { ReloadService } from '@shared/services/reload.service';
import { EndOfShiftFilesComponent } from './pages/complete-shift/components/end-of-shift-files/end-of-shift-files.component';
import { TrailerSelectComponent } from './components/trailer-select/trailer-select.component';
import {TrailersComponent} from "./pages/trailers/trailers.component";
import { CarrierSelectComponent } from './components/carrier-select/carrier-select.component';
import { BrowserProviderService } from '@shared/services/browser-provider.service';

// Trailers
import { TrailerNumberAdminInputComponent } from "./pages/trailers/components/trailer-number-admin-input/trailer-number-admin-input.component";
import { UacMiscInfoComponent } from './pages/inventory/components/uac-misc-info/uac-misc-info.component';
import { IgpsMiscInfoComponent } from './pages/inventory/components/igps-misc-info/igps-misc-info.component';


@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        InboundLoadsComponent,
        InboundLoadsFormComponent,
        OutboundLoadsComponent,
        OutboundLoadsFormComponent,
        InventoryComponent,
        PalletReclassificationComponent,
        CompleteShiftComponent,
        LoadsSummaryComponent,
        InternalTransfersComponent,
        ComponentsSummaryComponent,
        SortSummaryComponent,
        ChepActivityComponent,
        PieceRateFormComponent,
        ContextToolbarComponent,
        AddCarrierFormComponent,
        AddShippedToFormComponent,
        ChepMiscInfoComponent,
        UacMiscInfoComponent,
        IgpsMiscInfoComponent,
        BayInventoryComponent,
        SortLineInventoryComponent,
        RepairLineInventoryComponent,
        WalmartMiscInfoComponent,
        TpmInventoryComponent,
        ComponentsInventoryComponent,
        SuppliesInventoryComponent,
        ShipLoadComponent,
        CancelLoadComponent,
        PieceRateEntriesComponent,
        TeardownsScrapComponent,
        PieceRateSummaryComponent,
        PieceRateWeekStatusComponent,
        PieceRateAdjustmentCalculationComponent,
        TpmTrailerSummaryComponent,
        PieceRateResetComponent,
        WorkedShiftSelectorComponent,
        WwComponentsComponent,
        TrailersComponent,
        PieceRateGroupTotalsComponent,
        EndOfShiftFilesComponent,
        TrailerSelectComponent,
        TrailersComponent,
        TrailerSelectComponent,
        CarrierSelectComponent,
        TrailerNumberAdminInputComponent,
        ArriveInboundComponent,
        ProcessInboundComponent,
        UnloadInboundComponent,
        QualityControlConfigComponent,
        CreateOutboundLoadFromTemplateComponent
    ],
    imports: [
        BrowserModule,
        LuxonModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FormsModule,
        DynamicDialogModule,
        HttpClientModule,
        ButtonModule,
        AutoCompleteModule,
        CalendarModule,
        DropdownModule,
        FileUploadModule,
        InputTextModule,
        InputTextareaModule,
        SelectButtonModule,
        SplitButtonModule,
        StepsModule,
        ToolbarModule,
        TooltipModule,
        ToggleButtonModule,
        TableModule,
        OverlayPanelModule,
        DialogModule,
        ConfirmDialogModule,
        ToastModule,
        MessageModule,
        MessagesModule,
        CardModule,
        CheckboxModule,
        TabMenuModule,
        RadioButtonModule,
        DigitOnlyModule,
        MsalModule,
        AccordionModule,
        AppRoutingModule,

        SharedModule.forRoot(environment),
    ],
    providers: [{
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
    }, {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiInterceptor,
        multi: true
    }, {
        provide: MSAL_INSTANCE,
        useFactory: MsalInstanceFactory,
        deps: [LoggingService, SharedSettings, BrowserProviderService]
    }, {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MsalGuardConfigFactory,
        deps: [SharedSettings]
    }, {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MsalInterceptorConfigFactory,
        deps: [SharedSettings]
    },
        MsalGuard,
        MsalBroadcastService,
        MsalService,
        DialogService,
        ConfirmationService
    ],
    exports: [
    ],
    bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
    constructor(authService: AuthService, reloadService: ReloadService) {
        // check login early in site startup
        authService.isAuthenticated().subscribe(_ => { });
        reloadService.Start();
    }
}
